/**
 * PATH: packages/ui/src/kanban/components/card-detail/TagsSection.tsx
 * FILE_ID: UI-KANBAN-CMP-113
 * PURPOSE: Tag chips + picker + inline create section.
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode, useRef, useState } from 'react'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { LabelManagerPanel } from './LabelManagerPanel'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'
import { useRegisterUndo } from '../../undo/UndoContext'


// =============================================================================
// TagsSection — colored chips + picker + inline create
// =============================================================================

const TAG_COLOR_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
  '#8b5cf6', '#ec4899', '#64748b', '#0f172a',
]

export function TagsSection({
  card,
  orgTags,
  api,
  onChanged,
  onTagCreated,
  orgId,
  hideTitle = false,
}: {
  card: Card
  orgTags: Tag[]
  api: KanbanApi
  onChanged?: (card: Card) => void
  onTagCreated?: () => void
  orgId?: number
  /** ARC-4: suppress the inner h3 when a CollapsibleSection hosts this. */
  hideTitle?: boolean
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  // ARC-4 U09 — the picker panel's second mode: the label registry side
  // editor (rename / recolour / two-colour accent / archive / create).
  const [managing, setManaging] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(TAG_COLOR_PALETTE[7])
  const [error, setError] = useState<string | null>(null)

  // ARC-8 MR2 — label toggles register on the board's universal Ctrl+Z
  // stack (null when the drawer is opened outside a board surface). The
  // ref keeps undo thunks pointing at the CURRENT onChanged when an undo
  // fires many renders after registration.
  const registerUndo = useRegisterUndo()
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  const attached = card.tags.map((ct) => ct.tag).filter(Boolean) as Tag[]
  const attachedIds = new Set(attached.map((t) => t.id))
  const detached = orgTags.filter((t) => !attachedIds.has(t.id))

  const toggle = async (tag: Tag, isAttached: boolean) => {
    setBusy(tag.id)
    setError(null)
    try {
      const updated = isAttached
        ? await api.detachTag(card.id, tag.id)
        : await api.attachTag(card.id, tag.id, 'USER')
      onChanged?.(updated)
      // Register AFTER server success (undo can never race a failed
      // action). The compensating call routes through the same
      // audit-evented attach/detach endpoints — no shadow state.
      registerUndo?.({
        label: isAttached ? `Remove label "${tag.name}"` : `Add label "${tag.name}"`,
        undo: async () => {
          const c = isAttached
            ? await api.attachTag(card.id, tag.id, 'USER')
            : await api.detachTag(card.id, tag.id)
          onChangedRef.current?.(c)
        },
        redo: async () => {
          const c = isAttached
            ? await api.detachTag(card.id, tag.id)
            : await api.attachTag(card.id, tag.id, 'USER')
          onChangedRef.current?.(c)
        },
      })
    } catch (e) {
      // BUG-2026-06-29-KANBAN-LABEL-EDIT-SILENT-FAIL — surface a failed
      // attach/detach instead of stopping the spinner with no feedback.
      setError(
        e instanceof Error ? e.message : 'Could not update the label. Please try again.',
      )
    } finally {
      setBusy(null)
    }
  }

  const createTag = async () => {
    const name = newName.trim()
    if (!name || !orgId) return
    setBusy('__create__')
    setError(null)
    try {
      const path = name.toLowerCase().replace(/\s+/g, '-')
      const tag = await api.createTag({
        organisation: orgId,
        name,
        path,
        color: newColor,
      })
      // Immediately attach the freshly-created tag — the operator's
      // intent is "tag this card with X", so the create-then-pick
      // dance from the standalone Tags admin UI would feel slow.
      const updated = await api.attachTag(card.id, tag.id, 'USER')
      onChanged?.(updated)
      onTagCreated?.()
      setNewName('')
      setCreating(false)
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not create the label. Please try again.',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <section>
      <div className={`mb-1 flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
        {/* ARC-4: hideTitle when hosted inside a CollapsibleSection (which
            already renders the "Labels" header + count). */}
        {!hideTitle && (
          <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
            Labels
          </h3>
        )}
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-900"
        >
          <TagIcon className="h-3 w-3" /> Manage
        </button>
      </div>
      {attached.length === 0 ? (
        <p className="text-xs text-neutral-400">No labels yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {attached.map((t) => {
            const color = t.color || '#94a3b8'
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => void toggle(t, true)}
                disabled={busy === t.id}
                title={`${t.path} — click to remove`}
                className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium hover:opacity-80"
                style={{
                  background: `${color}26`,
                  color,
                  // ARC-4 U09 two-colour labels: the accent (when set)
                  // renders as the chip border; fill stays the wash/text.
                  border: `1px solid ${t.accent_color || `${color}55`}`,
                }}
              >
                <TagIcon className="h-2.5 w-2.5" />
                {t.name}
                {busy === t.id ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <X className="h-2.5 w-2.5" />
                )}
              </button>
            )
          })}
        </div>
      )}
      {pickerOpen && managing && (
        <div className="mt-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 p-1.5">
          <div className="mb-1 flex items-center justify-between px-0.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
              Edit labels
            </span>
            <button
              type="button"
              onClick={() => setManaging(false)}
              className="rounded px-1.5 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
            >
              Done
            </button>
          </div>
          <LabelManagerPanel
            orgTags={orgTags}
            api={api}
            orgId={orgId}
            onRegistryChanged={onTagCreated}
          />
        </div>
      )}
      {pickerOpen && !managing && (
        <div className="mt-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 p-1">
          <div className="max-h-40 overflow-y-auto">
            {detached.length === 0 ? (
              <p className="px-2 py-1 text-[11px] text-neutral-400">
                No more labels in this org.
              </p>
            ) : (
              detached.map((t) => {
                const color = t.color || '#94a3b8'
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => void toggle(t, false)}
                    disabled={busy === t.id}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <span
                      className="inline-block h-3 w-3 rounded"
                      style={{ background: color }}
                    />
                    <span className="truncate flex-1">{t.name}</span>
                    {busy === t.id && <Loader2 className="h-3 w-3 animate-spin" />}
                  </button>
                )
              })
            )}
          </div>
          {orgId !== undefined && (
            <div className="mt-1 border-t border-neutral-200 dark:border-neutral-800 pt-1.5">
              {!creating ? (
                <>
                  <button
                    type="button"
                    onClick={() => setCreating(true)}
                    className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                  >
                    <Plus className="h-3 w-3" /> Create new label
                  </button>
                  {/* ARC-4 U09 — the registry side editor (rename / recolour /
                      accent / archive). Client: "editing on the side to be
                      able to edit labels and create new ones". */}
                  <button
                    type="button"
                    onClick={() => setManaging(true)}
                    className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    <TagIcon className="h-3 w-3" /> Edit labels
                  </button>
                </>
              ) : (
                <div className="space-y-1.5 px-2 py-1.5">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Label name…"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void createTag()
                      if (e.key === 'Escape') setCreating(false)
                    }}
                    className="w-full rounded border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-1.5 py-1 text-xs"
                  />
                  <div className="flex flex-wrap gap-1">
                    {TAG_COLOR_PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewColor(c)}
                        aria-label={`Color ${c}`}
                        className={[
                          'h-4 w-4 rounded transition-transform',
                          newColor === c ? 'ring-2 ring-offset-1 ring-blue-500 scale-110' : '',
                        ].join(' ')}
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => void createTag()}
                      disabled={!newName.trim() || busy === '__create__'}
                      className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {busy === '__create__' && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCreating(false)
                        setNewName('')
                      }}
                      className="rounded px-2 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {error && (
        <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">{error}</p>
      )}
    </section>
  )
}
